// Shared helpers for writes that carry a list of ids.

/**
 * PostgREST puts the filter in the query string (`id=in.(...)`), so a bulk
 * update is bounded by the proxy's URI limit rather than by anything Postgres
 * cares about. A hundred uuids is roughly 3.7 KB of URL, comfortably inside the
 * usual 8 KB ceiling, and few enough requests that clearing a thousand-row
 * catalogue still feels immediate.
 */
export const ID_CHUNK = 100;

/** Deduplicated, blank-free ids in batches small enough to send. */
export function chunkIds(ids: Iterable<string>): string[][] {
  const unique = [...new Set(ids)].filter(Boolean);
  const out: string[][] = [];
  for (let i = 0; i < unique.length; i += ID_CHUNK) out.push(unique.slice(i, i + ID_CHUNK));
  return out;
}

/**
 * How many rows have their usage checked at once. Each check is itself several
 * parallel count requests, so this multiplies -- 4 here is 16 requests in
 * flight for products. Enough to keep a few hundred rows quick without leaning
 * on the gateway.
 */
export const USAGE_CONCURRENCY = 4;

/** Promise.all with a ceiling on how many run at once. Results keep input order. */
export async function mapLimit<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T) => Promise<R>,
): Promise<R[]> {
  const out = new Array<R>(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const i = next++;
      out[i] = await fn(items[i]);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return out;
}

/** What a bulk permanent delete did with each id it was handed. */
export interface BulkHardDeleteResult {
  /** Removed for good. */
  deleted: string[];
  /** Left alone because something references them. Deactivate these instead. */
  inUse: string[];
  /**
   * Referenced by nothing, yet the delete did not remove them: RLS refused --
   * a DELETE under RLS matches nothing rather than raising -- or someone else
   * deleted them first.
   */
  untouched: string[];
}
